import { useMemo } from "react";
import { METRICS_EVENTS, SUPPORTED_EXCHANGES } from "@cede/types";
import { useOnboarding } from "./useOnboarding";
import { ONBOARDING_MESSAGES } from "@/constants/onboarding";
import { useCampaigns } from "@/hooks/campaign/useCampaigns";
import { useTrackEvent } from "@/hooks/data/useMetricsEvents";
import { useHubUser } from "@/hooks/user/useHubUser";
import { ONBOARDING_STEPS } from "@/store/onboardingStore";
import { getCampaignCountsByExchange, hasExchangeAccounts } from "@/utils/onboarding";

export const useLinkCEXStep = () => {
  const { currentStep, isOpen } = useOnboarding();
  const { hubUser } = useHubUser();
  const isStepActive = currentStep === ONBOARDING_STEPS.LINK_CEX;

  // Only fetch campaigns when onboarding modal is open and this step is active
  const { campaigns } = useCampaigns({ status: ["live"], enabled: isOpen && isStepActive });

  // Check if user has CEX accounts connected
  const userHasExchangeAccounts = hasExchangeAccounts(hubUser);

  // Calculate campaign counts by exchange
  const campaignCountsByExchange = useMemo(
    () => getCampaignCountsByExchange(Object.values(SUPPORTED_EXCHANGES), campaigns ?? []),
    [campaigns],
  );

  // Determine message to show
  const message = userHasExchangeAccounts ? ONBOARDING_MESSAGES.ELIGIBLE_TO_TRADE : ONBOARDING_MESSAGES.CEX_DISCLAIMER;

  // Track when user views the CEX connection step during onboarding
  useTrackEvent({
    event: METRICS_EVENTS.ONBOARDING_CEX_STEP_VIEWED,
    properties: () => ({
      has_exchange_accounts: userHasExchangeAccounts,
      available_campaigns: campaigns?.length ?? 0,
    }),
    enabled: isStepActive,
  });

  return {
    isStepActive,
    campaignCountsByExchange,
    message,
  };
};
